import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ServicePricing, ServiceUsageSummary } from '../types';
import { listServicePricing, updateServicePricing, getServiceUsageSummary } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';

const ServicePricingConfig: React.FC = () => {
  const { t } = useTranslation('config');
  const toast = useToast();
  const [items, setItems] = useState<ServicePricing[]>([]);
  const [usage, setUsage] = useState<ServiceUsageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ creditsPerUnit: number; costPerUnit: number; enabled: boolean }>({ creditsPerUnit: 0, costPerUnit: 0, enabled: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listServicePricing(), getServiceUsageSummary()])
      .then(([p, u]) => { setItems(p || []); setUsage(u || []); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item: ServicePricing) => {
    setEditingType(item.serviceType);
    setEditForm({ creditsPerUnit: item.creditsPerUnit, costPerUnit: item.costPerUnit, enabled: item.enabled });
  };

  const handleSave = async () => {
    if (!editingType) return;
    setSaving(true);
    try {
      await updateServicePricing(editingType, editForm);
      toast.success(t('servicePricing.saveSuccess'));
      setEditingType(null);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const getUsage = (serviceType: string) => usage.find(u => u.serviceType === serviceType);

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1.5rem 1rem' }}>
      <Breadcrumb items={[{ label: t('servicePricing.breadcrumbTitle') }]} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>{t('servicePricing.pageTitle')}</h2>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-3)' }}>{t('servicePricing.loading')}</p>
      ) : items.length === 0 ? (
        <p style={{ color: 'var(--text-3)' }}>{t('servicePricing.noItems')}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem' }}>{t('servicePricing.serviceColumn')}</th>
              <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem' }}>{t('servicePricing.descColumn')}</th>
              <th style={{ textAlign: 'right', padding: '0.6rem 0.5rem' }}>{t('servicePricing.creditsPerUnit')}</th>
              <th style={{ textAlign: 'right', padding: '0.6rem 0.5rem' }}>{t('servicePricing.costPerUnit')}</th>
              <th style={{ textAlign: 'center', padding: '0.6rem 0.5rem' }}>{t('servicePricing.statusColumn')}</th>
              <th style={{ textAlign: 'right', padding: '0.6rem 0.5rem' }}>{t('servicePricing.callCount')}</th>
              <th style={{ textAlign: 'right', padding: '0.6rem 0.5rem' }}>{t('servicePricing.totalCredits')}</th>
              <th style={{ textAlign: 'center', padding: '0.6rem 0.5rem' }}>{t('servicePricing.actionsColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const u = getUsage(item.serviceType);
              const isEditing = editingType === item.serviceType;
              return (
                <tr key={item.serviceType} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.6rem 0.5rem', fontWeight: 500 }}>{item.displayName}</td>
                  <td style={{ padding: '0.6rem 0.5rem', color: 'var(--text-3)', fontSize: '0.82rem' }}>{item.description}</td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right' }}>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.creditsPerUnit}
                        onChange={e => setEditForm(f => ({ ...f, creditsPerUnit: Number(e.target.value) }))}
                        style={{ width: '70px', textAlign: 'right', border: '1px solid var(--primary)', borderRadius: '4px', padding: '2px 6px', fontSize: '0.85rem' }}
                      />
                    ) : item.creditsPerUnit}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right' }}>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.costPerUnit}
                        onChange={e => setEditForm(f => ({ ...f, costPerUnit: Number(e.target.value) }))}
                        style={{ width: '70px', textAlign: 'right', border: '1px solid var(--primary)', borderRadius: '4px', padding: '2px 6px', fontSize: '0.85rem' }}
                      />
                    ) : `¥${item.costPerUnit.toFixed(2)}`}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center' }}>
                    {isEditing ? (
                      <label style={{ cursor: 'pointer', fontSize: '0.82rem' }}>
                        <input type="checkbox" checked={editForm.enabled} onChange={e => setEditForm(f => ({ ...f, enabled: e.target.checked }))} /> {t('servicePricing.enabled')}
                      </label>
                    ) : (
                      <span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: '10px', background: item.enabled ? '#dcfce7' : '#fee2e2', color: item.enabled ? '#16a34a' : '#dc2626' }}>
                        {item.enabled ? t('servicePricing.enabled') : t('servicePricing.disabled')}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', color: 'var(--text-3)' }}>{u?.callCount ?? 0}</td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', color: 'var(--text-3)' }}>{u?.totalCredits ?? 0}</td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                        <button onClick={handleSave} disabled={saving} style={{ background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer' }}>
                          {saving ? t('servicePricing.saving') : t('servicePricing.save')}
                        </button>
                        <button onClick={() => setEditingType(null)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '4px', padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--text-2)' }}>
                          {t('servicePricing.cancel')}
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(item)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '4px', padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--text-2)' }}>
                        {t('servicePricing.edit')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ServicePricingConfig;
